<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useSystemAdminTemplateStore } from '@/stores/riskProfileTemplateStore';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Card from 'primevue/card';
import Tag from 'primevue/tag';
import Toast from 'primevue/toast';
import ConfirmDialog from 'primevue/confirmdialog';
import Skeleton from 'primevue/skeleton';
import InputText from 'primevue/inputtext';
import vTooltip from 'primevue/tooltip';
import type { ProfileGroup } from '@/types/risk-profile';
import { getEntityTypeIcon } from '@/utils/kyc';
import { formatDate } from '@/utils/formatters';
import BackButton from '@/components/base/BackButton.vue';
import { useRiskProfileOptions } from '@/composables/useRiskProfileOptions';
import { usePolicyDecisionDisplay } from '@/composables/usePolicyDecisionDisplay';
import KycPricingTableBadge from '@/components/accountAdmin/configuration/risk/KycPricingTableBadge.vue';
import AppPagination from '@/components/common/AppPagination.vue';
import { MODULE_ICONS } from '@/constants/icons';
import PolicyListFilters from '@/components/common/PolicyListFilters.vue';
import type { PolicyVisibilityFilter, PolicyProductionFilter } from '@/types/versioning';
import { productionFilterToIsActive } from '@/utils/versioning';

// --- Component Setup ---
const router = useRouter();
const store = useSystemAdminTemplateStore();
useRiskProfileOptions();

const { hasPolicyDecision, policyDecisionTooltip } = usePolicyDecisionDisplay(store);

// --- Server-side list controls (pagination / sort / search) ---
const SORT_FIELD_MAP: Record<string, string> = {
    profileName: 'name',
    profileType: 'entity_type',
    createdAt: 'created_at',
    last_updated_at: 'updated_at',
};

const searchText = ref(store.searchQuery);
// Search fires on blur / Enter (see the input's @blur + @keyup.enter), not on every keystroke.
const applySearch = () => store.setSearch(searchText.value);

const visibilityFilter = ref<PolicyVisibilityFilter>('active');
const productionFilter = ref<PolicyProductionFilter>('all');
watch(visibilityFilter, (val) => store.setStatusFilter(val));
watch(productionFilter, (val) => store.setIsActiveFilter(productionFilterToIsActive(val)));

const currentPage = computed(() => Math.floor(store.first / store.pageSize) + 1);

const onPageChange = (page: number) => {
    store.setPage((page - 1) * store.pageSize, store.pageSize);
};

const onRowsChange = (rows: number) => {
    // Reset to the first page when the page size changes.
    store.setPage(0, rows);
};

const onSort = (event: { sortField?: any; sortOrder?: number | null }) => {
    const field = typeof event.sortField === 'string' ? event.sortField : '';
    const backendField = field ? SORT_FIELD_MAP[field] ?? null : null;
    store.setSort(backendField, event.sortOrder === 1 ? 'asc' : 'desc');
};

onMounted(() => {
    store.fetchData();
});

const handleAddTemplate = () => {
    router.push({ name: 'system-admin-template-wizard' });
};

const onRowClick = (event: { data: ProfileGroup }) => {
    if (event.data) {
        router.push({ name: 'system-admin-template-details', params: { id: event.data.profileGroupId } });
    }
};

import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";

const confirm = useConfirm();
const toast = useToast();

const confirmDelete = (event: Event, group: ProfileGroup) => {
    event.stopPropagation();
    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: `Tem certeza que deseja excluir o modelo "${group.profileName}"? Esta ação não pode ser desfeita e só é permitida se o modelo nunca tiver sido utilizado em um relatório e não estiver ativo.`,
        header: 'Confirmar Exclusão',
        rejectLabel: 'Cancelar',
        acceptLabel: 'Excluir',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: async () => {
            try {
                await store.deleteProfile(group.profileGroupId);
                toast.add({ severity: 'success', summary: 'Sucesso', detail: 'Modelo excluído com sucesso.', life: 3000 });
            } catch (err: any) {
                const detail = err.response?.data?.detail || 'Não foi possível excluir o modelo.';
                toast.add({ severity: 'error', summary: 'Erro', detail, life: 5000 });
            }
        }
    });
};
</script>

<template>
  <!-- -m-6 cancels the app layout's p-6 (white) so the gray page background reaches the edges; p-6 pads the content inside it. -->
  <div class="-m-6 p-6 bg-gray-50 min-h-screen">
    <Toast />
    <ConfirmDialog :draggable="false" />
    <div class="max-w-7xl mx-auto">
      <header class="mb-6">
        <div class="flex flex-col sm:flex-row justify-between sm:items-center">
          <div class="flex items-center">
            <BackButton to="/settings" />
            <div>
              <h1 class="text-4xl font-bold text-gray-800 flex items-center gap-3">
                  <BaseIcon :size="36" class="text-primary-600">{{ MODULE_ICONS.kyx }}</BaseIcon>
                  Modelos KYX
              </h1>
              <p class="text-lg text-gray-500 mt-2">Crie e gerencie modelos globais de risco.</p>
            </div>
          </div>
          <Button label="Adicionar Novo Modelo" class="mt-4 sm:mt-0" @click="handleAddTemplate">
            <template #icon><span class="material-icons mr-2">add</span></template>
          </Button>
        </div>
      </header>
      <Card>
        <template #content>
          <DataTable
            :value="store.filteredTemplateGroups"
            :loading="store.isLoading"
            lazy
            :rows="store.pageSize"
            dataKey="profileGroupId"
            @row-click="onRowClick"
            @sort="onSort"
            class="p-datatable-clickable-rows"
          >
            <template #header>
              <div class="flex justify-between items-center flex-wrap gap-4">
                <div class="text-xl font-semibold text-gray-700">Todos os Modelos</div>
                <div class="flex items-center gap-4">
                  <PolicyListFilters
                    v-model:visibility="visibilityFilter"
                    v-model:production="productionFilter"
                  />
                  <span class="relative">
                    <span class="material-icons absolute top-1/2 -mt-3 left-3 text-surface-400">search</span>
                    <InputText v-model="searchText" placeholder="Buscar por nome" class="pl-10 font-normal" @blur="applySearch" @keyup.enter="applySearch"/>
                  </span>
                </div>
              </div>
            </template>
            <template #empty>
              <div class="text-center p-8">
                <span class="material-icons text-6xl text-gray-300 mb-2">folder_open</span>
                <h3 class="text-xl font-semibold text-gray-700">Nenhum Modelo Encontrado</h3>
                <p class="text-gray-500 mb-4">Comece criando o seu primeiro.</p>
                <Button label="Adicionar Novo Modelo" @click="handleAddTemplate">
                  <template #icon><span class="material-icons mr-2">add</span></template>
                </Button>
              </div>
            </template>
            <template #loading>
              <div class="p-4"><Skeleton v-for="i in 5" :key="i" height="3.5rem" class="mb-2"></Skeleton>
              </div>
            </template>

            <Column field="profileName" header="Nome do Modelo" sortable>
              <template #body="{ data }: { data: ProfileGroup }">
                <span class="font-semibold text-gray-800 group-hover:underline">{{ data.profileName }}</span>
              </template>
            </Column>

            <Column field="profileType" header="Tipo de Modelo" style="width: 8rem; text-align: center;" sortable>
              <template #body="{ data }: { data: ProfileGroup }">
                <BaseIcon class="text-[18px] text-gray-700">{{
                  getEntityTypeIcon(data.entity_type)
                }}
                </BaseIcon>
              </template>
            </Column>


            <Column header="Em Produção" style="width: 10rem; text-align: center;">
                <template #body="{ data }: { data: ProfileGroup }">
                    <span v-if="data.isActive" class="material-icons text-green-500" v-tooltip.top="'Este modelo tem uma versão em produção'">check_circle</span>
                    <span v-else class="material-icons text-gray-400" v-tooltip.top="'Nenhuma versão em produção'">do_not_disturb_on</span>
                </template>
            </Column>
            <Column header="Possui Rascunho" style="width: 8rem; text-align: center;">
                <template #body="{ data }: { data: ProfileGroup }">
                    <span v-if="store.getDraft(data)" class="material-icons text-yellow-500" v-tooltip.top="'Um rascunho está em andamento'">edit</span>
                </template>
            </Column>

            <Column header="Versão" field="currentInstanceId" style="width: 7rem">
              <template #body="{ data }: { data: ProfileGroup }">
                <Tag v-if="store.getCurrentInstance(data)" :value="`v${store.getCurrentInstance(data)?.version_number}`" severity="success" />
              </template>
            </Column>

            <Column header="Decisão Automática" style="width: 12rem; text-align: center;">
              <template #body="{ data }: { data: ProfileGroup }">
                <span v-if="hasPolicyDecision(data)" class="material-icons text-green-500" v-tooltip.top="policyDecisionTooltip(data)">gavel</span>
                <span v-else class="material-icons text-gray-400" v-tooltip.top="policyDecisionTooltip(data)">do_not_disturb_on</span>
              </template>
            </Column>

            <Column header="Tokens" style="width: 14rem">
                <template #body="{ data }: { data: ProfileGroup }">
                    <KycPricingTableBadge :group="data" :store="store" />
                </template>
            </Column>
            <Column header="Criado em" sortable field="createdAt" style="width: 10rem">
              <template #body="{ data }: { data: ProfileGroup }">{{ formatDate(data.createdAt) }}</template>
            </Column>

            <Column header="Última Atualização" sortable field="last_updated_at" style="width: 10rem">
              <template #body="{ data }: { data: ProfileGroup }">
                {{ formatDate(data.last_updated_at) }}
              </template>
            </Column>

            <Column style="width: 8rem; text-align: right;">
                <template #body="{ data }: { data: ProfileGroup }">
                    <div class="flex items-center justify-end gap-2">
                        <Button 
                            v-if="!data.isActive"
                            severity="danger" 
                            text 
                            rounded 
                            v-tooltip.top="'Excluir Modelo'"
                            @click="confirmDelete($event, data)"
                        >
                          <template #icon>
                            <BaseIcon>delete</BaseIcon>
                          </template>
                        </Button>
                        <span class="material-icons text-gray-300 ml-1 group-hover:text-blue-500 transition-colors">chevron_right</span>
                    </div>
                </template>
            </Column>
          </DataTable>
          <AppPagination
            v-if="store.totalRecords > 0"
            :totalRecords="store.totalRecords"
            :rows="store.pageSize"
            :page="currentPage"
            :loading="store.isLoading"
            @page-change="onPageChange"
            @rows-change="onRowsChange"
          />
        </template>
      </Card>
    </div>
  </div>
</template>

<style scoped>
.p-datatable-clickable-rows .p-datatable-tbody > tr { cursor: pointer; transition: background-color 0.2s; }
.p-datatable-clickable-rows .p-datatable-tbody > tr:not(.p-datatable-emptymessage):hover { background-color: var(--surface-50) !important; }
</style>
